// NOTE: this module was import-free until TGCLOUD-1692. It now takes three, and all are
// leaves — `config` and `reproductiveSex` import nothing at all, and `twinLinkage`
// imports only `config` — so no cycle and no weight. The alternative was a fifth
// hand-rolled copy of the twin-field write, which is what this ticket exists to remove.
import { TWIN_TYPE } from './config';
import { joinTwinSet } from './twinLinkage';
import {
  PARENT_SLOT,
  SEX,
  reproductiveSexOf,
  parentSlotFor,
  parentSlotAcrossUnions,
  otherParentSlot,
  sexForParentSlot
} from './reproductiveSex';

// Pure, client-side helpers that keep a pedigree member array's denormalised
// edge arrays consistent with the per-child parent back-pointers AT
// CONSTRUCTION TIME. This is the front-end analogue of
// backend/util/pedigreeEdges.js (which only runs as a server safety net at
// persist time, children-only, and so cannot fix the live in-memory array nor
// I2/I4 violations).
//
// A member's parent<->child edge is stored twice:
//   - CHILD back-pointer:  fatherId / motherId   (SOURCE OF TRUTH)
//   - PARENT forward list: childrenIds
// and they must agree:
//   I1 forward: c.fatherId/motherId === P.pid  =>  P.childrenIds includes c.pid
//   I2 back:    c.pid in P.childrenIds          =>  c.fatherId===P.pid || c.motherId===P.pid
//   I4 sibling: members sharing >=1 parent are mutual entries in siblingIds
// The add-relative / set-relation paths build these by hand; these helpers
// derive both sides from a single decision so they cannot diverge.

const uniq = (arr) => [...new Set(arr)];

// Resolve a new child's parent back-pointers from the selected node and the
// chosen co-parent (the node's current-union spouse, or null).
//
// The slots follow REPRODUCTIVE SEX through the one shared rule, parentSlotFor
// (TGCLOUD-1786, D2), so the native add-child agrees with both FHQ derivations:
// the node takes its own sex's slot; an UNKNOWN-sex node takes the complement of
// the spouse's sex; both unknown, or no spouse, → father. (This used to send every
// unknown-sex node to the mother slot, while the FHQ sent it to father.)
//
// The spouse takes the OTHER slot unless its own known sex contradicts it — a
// same-sex couple's spouse is not written into the opposite-sex slot, because the
// data model's father/mother slots are sex-typed. An unknown-sex spouse does NOT
// contradict either slot: under D2 it takes the complement of its partner's, which
// is exactly the other slot, so it is a co-parent. (It used to be left out.)
//
// `nodeSex` is the node's reproductive sex — addChildrenToRecords passes
// reproductiveSexOf(self) — and is read case-insensitively; the spouse's is
// resolved here from its record. `coParentId` is the spouse pid IFF the spouse
// actually occupies a slot — both the child's back-pointer AND the spouse's
// childrenIds entry must be derived from it, so a same-sex spouse never ends up
// listing a child that does not point back to it (I2).
//
// `decidedSlot`, when given, is the slot the caller has already decided for the node
// — addChildrenToRecords decides it ACROSS the node's unions for an unknown-sex
// node (ownParentSlotOf), so this couple's spouse does not get to flip it. The
// spouse is then a co-parent unless its known sex contradicts the other slot, the
// same rule as for a same-sex couple.
export const resolveChildParents = (
  nodeSex,
  ownerPid,
  spouse,
  decidedSlot = null
) => {
  const spouseSex = spouse ? reproductiveSexOf(spouse) : null;
  const nodeSlot = decidedSlot || parentSlotFor(nodeSex, spouseSex);
  // The sex the node's slot implies is the one the spouse must NOT have.
  const coParentId =
    spouse && spouseSex !== sexForParentSlot(nodeSlot) ? spouse.pid : null;
  if (nodeSlot === PARENT_SLOT.father) {
    return { fatherId: ownerPid, motherId: coParentId, coParentId };
  }
  return { fatherId: coParentId, motherId: ownerPid, coParentId };
};

// The slot `pid`'s existing children give it, by their back-pointers (the source
// of truth): the majority, or null with no children or as many each way.
const childrenSlotOf = (members, pid) => {
  let asFather = 0;
  let asMother = 0;
  for (const p of members || []) {
    if (p.fatherId === pid) asFather += 1;
    else if (p.motherId === pid) asMother += 1;
  }
  if (asFather > asMother) return PARENT_SLOT.father;
  if (asMother > asFather) return PARENT_SLOT.mother;
  return null;
};

// The sex a parent counts as when linkSiblingRelation has to choose which way
// round a couple sits: its reproductive sex, else — for an unknown-sex parent —
// the sex its slot among its OTHER children implies (the one-slot-across-unions
// rule), leaving out the two people being linked, whose disagreement is the
// question. Unknown when neither says.
const parentSexForLink = (members, parent, excludePids) => {
  if (!parent) return SEX.unknown;
  const sex = reproductiveSexOf(parent);
  if (sex !== SEX.unknown) return sex;
  const slot = childrenSlotOf(
    (members || []).filter((p) => !excludePids.has(p.pid)),
    parent.pid
  );
  return slot ? sexForParentSlot(slot) : SEX.unknown;
};

// A node's FIRST partner (TGCLOUD-1786): the first entry of its spouseIds that
// resolves to a member. This is the one definition the native writers use; the
// FHQ derivations use the first member labelled `spouse` (see
// parentSlotAcrossUnions in reproductiveSex.js).
export const firstPartnerOf = (members, node) => {
  for (const id of node?.spouseIds || []) {
    const partner = (members || []).find((p) => p.pid === id);
    if (partner) return partner;
  }
  return null;
};

// The sex a partner counts as for D2: its reproductive sex, else — for an
// unknown-sex partner that is already a parent — the sex its own established
// slot implies, so two unknown-sex partners come out as one couple rather than
// both fathers.
const partnerSexForSlot = (members, partner) => {
  if (!partner) return null;
  const sex = reproductiveSexOf(partner);
  if (sex !== SEX.unknown) return sex;
  const slot = childrenSlotOf(members, partner.pid);
  return slot ? sexForParentSlot(slot) : SEX.unknown;
};

// The ONE slot `node` holds as a parent, across all its unions (TGCLOUD-1786,
// Tim's decision). A known reproductive sex decides it: male → father, female →
// mother. An UNKNOWN-sex node keeps the slot its existing children already give
// it, so a spouse added now adopts those children into the slot they left empty,
// and every later child — with this partner or any other — comes out the same
// way round. With no children yet (or as many each way, which only data written
// before this rule can hold), D2 against its FIRST partner (firstPartnerOf), and
// father with no partner at all.
export const ownParentSlotOf = (members, node) => {
  const sex = reproductiveSexOf(node);
  if (sex !== SEX.unknown || !node) return parentSlotFor(sex, null);
  return parentSlotAcrossUnions(
    sex,
    partnerSexForSlot(members, firstPartnerOf(members, node)),
    childrenSlotOf(members, node.pid)
  );
};

// Append newly-created sibling pids to the right existing members after adding
// sibling(s) of an existing node. A member can be BOTH a co-sibling (gets
// siblingIds) AND a shared parent (gets childrenIds) in a consanguinity loop,
// so the two updates are applied INDEPENDENTLY — never as a mutually-exclusive
// if/else, which would skip a parent's childrenIds when that parent is also
// classified a sibling (I1).
export const appendSiblingEdges = (
  members,
  { siblingGroupPids, fatherId, motherId, newSiblingPids }
) => {
  const group = new Set(siblingGroupPids);
  return members.map((person) => {
    const inGroup = group.has(person.pid);
    const isParent =
      (fatherId != null && person.pid === fatherId) ||
      (motherId != null && person.pid === motherId);
    if (!inGroup && !isParent) return person;
    const next = { ...person };
    if (inGroup) {
      next.siblingIds = uniq([...(person.siblingIds || []), ...newSiblingPids]);
    }
    if (isParent) {
      next.childrenIds = uniq([
        ...(person.childrenIds || []),
        ...newSiblingPids
      ]);
    }
    return next;
  });
};

// Next free numeric pid for a member array (collision-proof when called inside a
// setMemberRecords updater, where the array is always the latest state).
export const nextPidFrom = (members) => {
  let max = 0;
  for (const p of members || []) {
    if (typeof p.pid === 'number' && p.pid > max) max = p.pid;
  }
  return max + 1;
};

// PURE add-sibling(s) transform. Resolves the selected person and all parent /
// sibling / pid data FROM `members` (the array passed in), never from a captured
// closure — so rapid successive adds are correct regardless of React render
// timing (the bug where a fast second add read a stale selected-node and dropped
// the sibling at the parents' generation / with no parent edges). Call it as
// `setMemberRecords((prev) => addSiblingsToRecords(prev, selectedPid, opts))`.
//
// opts: { count=1, twinType, twinSubGroupId, makeSibling }
//   makeSibling() -> a fresh person object WITHOUT a pid (uid/gender/relationship
//   set, empty edge arrays); this function assigns sequential collision-free pids.
//   twinSubGroupId is supplied by the caller (kept out of here so the transform is
//   deterministic/testable), and is only a SUGGESTION — an id already on the set wins.
//
// `twinType` ALONE decides whether the new siblings are co-multiples (TGCLOUD-1692).
// It used to take `isTwin` and `siblingType` as well, and all three had to agree:
// callers computed `isTwin: siblingType === SIBLING_TYPE.twin` and passed a
// `siblingType` that is now derived from the size of the resulting set, so two of the
// three were redundant and the third was the one observed to be wrong (a set of three
// labelled `twin`). Both are still accepted-and-ignored if passed, so no caller breaks.
export const addSiblingsToRecords = (
  members,
  selectedPid,
  { count = 1, twinType = null, twinSubGroupId = null, makeSibling } = {}
) => {
  const list = members || [];
  const self = list.find((p) => p.pid === selectedPid);
  if (!self || count < 1 || typeof makeSibling !== 'function') return list;
  // "Not a twin" is a real choice in the context menu and arrives as `singleton`.
  const asMultiple = !!twinType && twinType !== TWIN_TYPE.singleton;

  // Fresh, sequential pids derived from the LATEST array.
  let pid = nextPidFrom(list);
  const newSiblings = [];
  for (let i = 0; i < count; i++) {
    const sib = makeSibling();
    sib.pid = pid++;
    sib.fatherId = self.fatherId ?? null;
    sib.motherId = self.motherId ?? null;
    newSiblings.push(sib);
  }
  const newSiblingPids = newSiblings.map((s) => s.pid);

  // Sibling group by SHARED PARENTS of the FRESH self (with the parentless
  // fallback to the explicit siblingIds, matching the single-add path).
  const noParents = self.fatherId == null && self.motherId == null;
  const groupPids = noParents
    ? uniq([self.pid, ...(self.siblingIds || [])])
    : list
        .filter((p) => {
          if (p.pid === self.pid) return true;
          const sameFather =
            self.fatherId != null && p.fatherId === self.fatherId;
          const sameMother =
            self.motherId != null && p.motherId === self.motherId;
          return sameFather || sameMother;
        })
        .map((p) => p.pid);

  // Twin membership is stamped AFTER insertion, by joinTwinSet — see the note below.
  // `twinSubGroupId` is still honoured as the caller's suggested id for a brand-new set.

  // I1/I4 edge arrays for the existing members (shared parents + sibling group).
  let arr = appendSiblingEdges(list, {
    siblingGroupPids: groupPids,
    fatherId: self.fatherId ?? null,
    motherId: self.motherId ?? null,
    newSiblingPids
  });

  // New siblings reference the whole sibship (existing group + each other).
  for (const sib of newSiblings) {
    const ids = new Set(sib.siblingIds || []);
    for (const g of groupPids) if (g !== sib.pid) ids.add(g);
    for (const other of newSiblings)
      if (other.pid !== sib.pid) ids.add(other.pid);
    sib.siblingIds = [...ids];
  }

  // Insert immediately after the last existing member of the sibship (keeps the
  // new siblings adjacent to their group), else right after the selected person.
  let lastIdx = -1;
  arr.forEach((m, idx) => {
    if (groupPids.includes(m.pid) && idx > lastIdx) lastIdx = idx;
  });
  const insertIdx =
    lastIdx >= 0 ? lastIdx + 1 : arr.findIndex((m) => m.pid === self.pid) + 1;
  const withNew = [
    ...arr.slice(0, insertIdx),
    ...newSiblings,
    ...arr.slice(insertIdx)
  ];
  if (!asMultiple) return withNew;

  // TWIN MEMBERSHIP THROUGH THE ONE WRITER (TGCLOUD-1692).
  //
  // This used to stamp the fields by hand, and got a set of three wrong in two ways at
  // once. The new sibling received `twinRelations: [{ withPid: self.pid }]` — a relation
  // to the person right-clicked and to NOBODY ELSE — so right-clicking one of an
  // existing pair and adding an identical twin left the other member of that pair and
  // the newcomer with no relation between them, hence no bar. And `siblingType` was
  // taken verbatim from the caller, which for the context menu is always
  // SIBLING_TYPE.twin, so all three were labelled `twin`. Verified, not theorised. The
  // set was then unrepairable from the sidebar, because `twinCandidatesFor` excludes
  // anyone already carrying a confirmed group.
  //
  // `joinTwinSet` closes the graph over the whole set, sizes `siblingType` from the
  // members actually present, and — importantly now zygosity is per pair — asserts
  // `twinType` ONLY for the pairs this call creates, leaving anything already recorded
  // between the existing members alone.
  //
  // It runs LAST, after appendSiblingEdges and after insertion, because the fields it
  // writes are keyed on members being present in the array; the ordering that used to
  // matter (self's `siblingIds` surviving the twin write) is no longer a concern, since
  // joinTwinSet touches twin fields only.
  const seeded = twinSubGroupId
    ? withNew.map((m) =>
        newSiblingPids.includes(m.pid) && !m.twinSubGroupId
          ? { ...m, twinSubGroupId: self.twinSubGroupId || twinSubGroupId }
          : m
      )
    : withNew;
  return joinTwinSet(seeded, self.pid, newSiblingPids, twinType);
};

// PURE add-child(ren) transform — same rationale as addSiblingsToRecords. The
// co-parent, the sex-typed parent slots, the existing-sibship, and the new pids
// are ALL resolved from the latest `members`, so rapid son/daughter adds can't
// read a stale selected-node or collide on pid. Call inside the updater:
// `setMemberRecords((prev) => addChildrenToRecords(prev, selectedPid, opts))`.
// opts: { count=1, makeChild, coParentPid, gestational } — makeChild() returns a
// fresh person WITHOUT a pid; coParentPid targets a specific union on a
// multi-spouse node (must be one of self.spouseIds; anything else falls back to
// the most recent spouse, the historical default). `gestational: true` says the
// node GAVE BIRTH to these children (a fetus converted to a person): a node of
// unknown sex is then the mother, whatever D2 would say from its partner — the
// pregnancy record is better evidence than the partner's sex. A recorded sex
// still decides, as it always has.
//
// ONE SLOT ACROSS UNIONS (TGCLOUD-1786, Tim's decision). An unknown-sex node's
// slot is not decided per couple any more: it is ownParentSlotOf — the slot its
// existing children already give it, else D2 against its FIRST partner — so a
// child with a second partner of the other sex no longer makes it the father of
// one sibship and the mother of the other. That second partner, if its known sex
// contradicts the other slot, is left out of the child's slots exactly as a
// same-sex spouse always has been.
export const addChildrenToRecords = (
  members,
  selectedPid,
  { count = 1, makeChild, coParentPid = null, gestational = false } = {}
) => {
  const list = members || [];
  const self = list.find((p) => p.pid === selectedPid);
  if (!self || count < 1 || typeof makeChild !== 'function') return list;

  const spouseId =
    coParentPid != null && self.spouseIds?.includes(coParentPid)
      ? coParentPid
      : self.spouseIds?.length
      ? self.spouseIds[self.spouseIds.length - 1]
      : null;
  const spouse = spouseId != null ? list.find((p) => p.pid === spouseId) : null;
  // coParentId is the spouse only when it actually occupies a parent slot; both
  // the child back-pointer AND the spouse's childrenIds derive from it (I1/I2).
  // The node's REPRODUCTIVE sex (gender, else the sex its identity implies), not
  // the raw field, so a record with gender unset and identity "Male" is a father.
  const ownSex = reproductiveSexOf(self);
  let nodeSlot = null;
  if (ownSex === SEX.unknown && gestational) {
    nodeSlot = PARENT_SLOT.mother;
  } else if (ownSex === SEX.unknown) {
    // Both partners of unknown sex: whichever of them is ADDING would otherwise
    // decide, so a child added from each side could come out opposite ways round
    // — full siblings with different ordered parents, which blocks twinning and
    // the sibling link. One couple has one orientation: keep the one its
    // existing children already have (this matters only for data written before
    // the one-slot rule, where the node may hold both slots across its unions).
    if (spouse && reproductiveSexOf(spouse) === SEX.unknown) {
      const existing = list.find(
        (p) =>
          (p.fatherId === selectedPid && p.motherId === spouseId) ||
          (p.fatherId === spouseId && p.motherId === selectedPid)
      );
      if (existing) {
        nodeSlot =
          existing.fatherId === selectedPid
            ? PARENT_SLOT.father
            : PARENT_SLOT.mother;
      }
    }
    if (!nodeSlot) nodeSlot = ownParentSlotOf(list, self);
  }
  const { coParentId, fatherId, motherId } = resolveChildParents(
    ownSex,
    selectedPid,
    spouse,
    nodeSlot
  );

  let pid = nextPidFrom(list);
  const newChildren = [];
  for (let i = 0; i < count; i++) {
    const child = makeChild();
    child.pid = pid++;
    child.fatherId = fatherId;
    child.motherId = motherId;
    newChildren.push(child);
  }
  const newChildIds = newChildren.map((c) => c.pid);

  const existingSibPids = new Set(
    list
      .filter((p) => p.fatherId === fatherId && p.motherId === motherId)
      .map((p) => p.pid)
  );

  const arr = list.map((person) => {
    if (person.pid === selectedPid) {
      return {
        ...person,
        childrenIds: uniq([...(person.childrenIds || []), ...newChildIds])
      };
    }
    if (coParentId != null && person.pid === coParentId) {
      return {
        ...person,
        childrenIds: uniq([...(person.childrenIds || []), ...newChildIds])
      };
    }
    if (existingSibPids.has(person.pid)) {
      return {
        ...person,
        siblingIds: uniq([...(person.siblingIds || []), ...newChildIds])
      };
    }
    return person;
  });

  for (const child of newChildren) {
    const ids = new Set(child.siblingIds || []);
    for (const s of existingSibPids) ids.add(s);
    for (const other of newChildren) {
      if (other.pid !== child.pid) ids.add(other.pid);
    }
    child.siblingIds = [...ids];
  }

  return [...arr, ...newChildren];
};

// What a NEW spouse of `node` becomes: the slot it fills (the other one), the
// child field that slot is written to when it adopts a child, and the sex to
// create it with — the sex its slot implies, so the couple is consistent and
// resolveChildParents keeps giving the node the same slot. Both addSpouse copies
// (addRelativeActions.js, AddRelativeMenu.jsx) and adoptableChildrenForNewSpouse
// read this ONE answer; they used to test `gender === 'male'` each on their own,
// which made a new spouse of an unknown-sex parent MALE while add-child had put
// that parent in the father slot — so the first children were never adopted and
// the next child put the parent in the mother slot (TGCLOUD-1786 review).
export const newSpouseSlotsFor = (members, node) => {
  const nodeSlot = ownParentSlotOf(members, node);
  const spouseSlot = otherParentSlot(nodeSlot);
  return {
    nodeSlot,
    spouseSlot,
    spouseSlotKey: spouseSlot === PARENT_SLOT.father ? 'fatherId' : 'motherId',
    spouseGender: sexForParentSlot(spouseSlot)
  };
};

// Children of `node` whose OTHER parent slot is still empty — these become
// children of a newly added FIRST spouse too. Children that already have a real
// other parent must NOT be re-parented onto (or copied into the childrenIds of)
// the new spouse: that would steal the child from / orphan the existing parent's
// edge (I2). Returns [] when the node already has a spouse (no re-parenting).
// "Other" is the slot newSpouseSlotsFor gives the spouse, never a raw gender test.
export const adoptableChildrenForNewSpouse = (members, node) => {
  if (!node || (node.spouseIds && node.spouseIds.length > 0)) return [];
  const childIds = new Set(node.childrenIds || []);
  const { spouseSlotKey } = newSpouseSlotsFor(members, node);
  return (members || [])
    .filter((p) => childIds.has(p.pid) && p[spouseSlotKey] == null)
    .map((p) => p.pid);
};

// Pure transform for the drag-to-connect "Set Relation -> Child" path. Sets the
// child's missing parent back-pointer, keeps the chosen parent's (and, where
// applicable, that parent's first spouse's) childrenIds in lockstep, and — the
// fix — only lists the child under the spouse when the child actually
// back-points to that spouse (I2). Validation mirrors the original handler.
//
// It also writes siblingIds between the child and its new FULL siblings (the same
// ordered parent pair, as addChildrenToRecords does) — invariant I4, and not a
// nicety: the layout slots a person whose stored rank was just dropped
// (structuralEditOrder.js) by birth order among its RANKED siblings, and it finds
// them through siblingIds (layoutV2 birthSlotKey). Without them the attached
// person is parked at the row edge (TGCLOUD-1786 §7 Phase 2).
// Returns { error } | { members, touchedPids }.
export const linkChildRelation = (members, childPid, parentPid) => {
  const deepCopy = (members || []).map((m) => ({ ...m }));
  const childIdx = deepCopy.findIndex((p) => p.pid === childPid);
  const parentIdx = deepCopy.findIndex((p) => p.pid === parentPid);
  if (childIdx < 0 || parentIdx < 0) return { members: null };

  const child = { ...deepCopy[childIdx] };
  const parent = { ...deepCopy[parentIdx] };

  if (child.fatherId && child.motherId && child.noparents !== 'true') {
    return { error: 'Relation Not Allowed : Node already has parents' };
  }

  const spouseId = parent.spouseIds?.[0] ?? null;
  const spouseIdx =
    spouseId != null ? deepCopy.findIndex((p) => p.pid === spouseId) : -1;

  // The parent's slot follows REPRODUCTIVE sex (TGCLOUD-1786, D2). A known sex
  // decides it outright, as before. An unknown-sex parent used to get NO slot at
  // all — listed in its childrenIds with no back-pointer (an I2 break, and the
  // child never drew under it). It now keeps ONE slot across all its unions: the
  // slot its existing children already give it wins, even when that means
  // refusing a child whose parent in that slot is someone else (below). With no
  // children yet it takes the slot the child still has free when the child
  // already has one parent — that parent IS this union's partner — and otherwise
  // D2 against its first partner (ownParentSlotOf), father with none.
  const parentSlot = (() => {
    const sex = reproductiveSexOf(parent);
    if (sex !== SEX.unknown) return parentSlotFor(sex, null);
    if (child.fatherId === parent.pid) return PARENT_SLOT.father;
    if (child.motherId === parent.pid) return PARENT_SLOT.mother;
    const established = childrenSlotOf(deepCopy, parent.pid);
    if (established) return established;
    if (child.motherId && !child.fatherId) return PARENT_SLOT.father;
    if (child.fatherId && !child.motherId) return PARENT_SLOT.mother;
    return ownParentSlotOf(deepCopy, parent);
  })();
  if (parentSlot === PARENT_SLOT.mother) {
    if (child.motherId && child.motherId !== parent.pid) {
      return {
        error: 'Relation Not Allowed : Child already has a different mother'
      };
    }
    child.motherId = parent.pid;
  } else {
    if (child.fatherId && child.fatherId !== parent.pid) {
      return {
        error: 'Relation Not Allowed : Child already has a different father'
      };
    }
    child.fatherId = parent.pid;
  }

  parent.childrenIds = uniq([...(parent.childrenIds || []), child.pid]);
  deepCopy[parentIdx] = parent;
  const touched = new Set([childPid, parentPid]);

  if (spouseIdx >= 0) {
    const spouse = { ...deepCopy[spouseIdx] };
    // The spouse fills the free slot only when it is a co-parent by the SAME rule
    // the native add-child applies (resolveChildParents): a spouse whose known sex
    // is the sex of the parent's own slot — a same-sex couple — is left out rather
    // than written into the slot that contradicts it. This used to fill the slot
    // whatever the spouse's sex, so the two native writers disagreed.
    const { coParentId } = resolveChildParents(
      reproductiveSexOf(parent),
      parent.pid,
      spouse,
      parentSlot
    );
    if (coParentId != null) {
      if (parentSlot === PARENT_SLOT.mother && !child.fatherId)
        child.fatherId = spouseId;
      if (parentSlot === PARENT_SLOT.father && !child.motherId)
        child.motherId = spouseId;
    }
    // Only claim the child under the spouse when the child actually back-points
    // to it — otherwise the spouse's childrenIds would gain an orphaned entry
    // (e.g. a child that already had its real other parent recorded). (I2)
    if (child.fatherId === spouseId || child.motherId === spouseId) {
      spouse.childrenIds = uniq([...(spouse.childrenIds || []), child.pid]);
      deepCopy[spouseIdx] = spouse;
      touched.add(spouseId);
    }
  }

  // I4: the child and its full siblings — the same ordered parent pair — list
  // each other. Only a sibling whose list actually changes is touched, so the
  // set-relation POST carries no more nodes than it has to.
  const fullSiblingPids = deepCopy
    .filter(
      (p) =>
        p.pid !== child.pid &&
        (p.fatherId ?? null) === (child.fatherId ?? null) &&
        (p.motherId ?? null) === (child.motherId ?? null)
    )
    .map((p) => p.pid);
  if (fullSiblingPids.length) {
    child.siblingIds = uniq([...(child.siblingIds || []), ...fullSiblingPids]);
    fullSiblingPids.forEach((pid) => {
      const idx = deepCopy.findIndex((p) => p.pid === pid);
      const sibling = deepCopy[idx];
      if ((sibling.siblingIds || []).includes(child.pid)) return;
      deepCopy[idx] = {
        ...sibling,
        siblingIds: [...(sibling.siblingIds || []), child.pid]
      };
      touched.add(pid);
    });
  }

  deepCopy[childIdx] = child;
  return { members: deepCopy, touchedPids: [...touched] };
};

// Pure transform for the drag-to-connect "Set Relation -> Sibling" path. Copies
// the parent back-pointers from whichever node already has parents onto the
// other, then — the fix — rebuilds childrenIds on the shared parents (I1) and
// siblingIds across the whole resulting sibship (I4); the original handler set
// only the back-pointers.
//
// The parents are compared as an UNORDERED COUPLE (TGCLOUD-1786 §6.5). This used
// to compare motherId alone, so two people with the same mother and DIFFERENT
// fathers passed, and the second silently inherited the first's father; and a
// node whose only recorded parent was its father counted as having none.
//
// Now the two nodes' recorded parents, pooled, must be at most two people — one
// couple — and the result is that couple in ONE orientation, chosen without
// regard to which node was clicked first:
//   - the orientation that keeps every parent each node already records in the
//     slot it records it in (a slot left empty on one side is filled from the
//     other: a father-only node and a mother-only node of the same couple both
//     get the couple, and nobody is dropped);
//   - when there is none — the same couple recorded the other way round on one
//     side, which pre-1786 data holds (§5), or a single parent recorded as father
//     on one side and mother on the other — the orientation that agrees with
//     recorded sex (parentSexForLink): no female in the father slot, no male in
//     the mother slot. If both orientations or neither qualify, sex cannot say
//     which is right, and the link is refused rather than decided by click order.
// Only the two linked nodes are re-oriented; any other child of the couple keeps
// what it records.
//
// A `noparents` node's pointers are legacy placeholders, so it counts as having
// none, as it always did as a source.
// Returns { error } | { members, touchedPids }.
export const linkSiblingRelation = (members, currentPid, siblingPid) => {
  const deepCopy = (members || []).map((m) => ({ ...m }));
  const currentIdx = deepCopy.findIndex((p) => p.pid === currentPid);
  const siblingIdx = deepCopy.findIndex((p) => p.pid === siblingPid);
  if (currentIdx < 0 || siblingIdx < 0) return { members: null };

  const current = { ...deepCopy[currentIdx] };
  const sibling = { ...deepCopy[siblingIdx] };

  // Truthy, as the old motherId test was: pids start at 1, and a legacy '' is none.
  const slotsOf = (p) =>
    p.noparents ? [null, null] : [p.fatherId || null, p.motherId || null];
  const currentSlots = slotsOf(current);
  const siblingSlots = slotsOf(sibling);
  const couple = uniq(
    [...currentSlots, ...siblingSlots].filter((id) => id != null)
  );

  if (!couple.length) {
    return {
      error: 'Relation Not Allowed : At least one node must have parents'
    };
  }
  const differentParents = {
    error: 'Relation Not Allowed : Both nodes have different parents'
  };
  if (couple.length > 2) return differentParents;

  // The two ways the couple can sit in [father, mother].
  const [a, b = null] = couple;
  const orientations = [
    [a, b],
    [b, a]
  ];
  // Every parent the node records stays in the slot it records it in.
  const keeps = (slots, orientation) =>
    slots.every((id, i) => id == null || id === orientation[i]);
  const kept = orientations.filter(
    (o) => keeps(currentSlots, o) && keeps(siblingSlots, o)
  );

  let orientation = kept[0] || null;
  if (!orientation) {
    // Recorded the other way round on one side. Sex decides, or nothing does.
    const others = new Set([currentPid, siblingPid]);
    const byPidOf = (pid) => deepCopy.find((p) => p.pid === pid) || null;
    const sexOf = (pid) =>
      pid == null ? null : parentSexForLink(deepCopy, byPidOf(pid), others);
    const agreesWithSex = ([father, mother]) =>
      sexOf(father) !== SEX.female && sexOf(mother) !== SEX.male;
    const bySex = orientations.filter(agreesWithSex);
    if (bySex.length !== 1) {
      const sameCouple =
        currentSlots.filter(Boolean).length === 2 &&
        siblingSlots.filter(Boolean).length === 2;
      return sameCouple || couple.length === 1
        ? {
            error:
              'Relation Not Allowed : Both nodes have the same parents recorded in opposite father/mother roles, and their recorded sex cannot say which is right'
          }
        : differentParents;
    }
    orientation = bySex[0];
  }
  const [fatherId, motherId] = orientation;

  current.motherId = motherId;
  current.fatherId = fatherId;
  sibling.motherId = motherId;
  sibling.fatherId = fatherId;

  delete current.top_level;
  delete current.noparents;
  delete sibling.top_level;
  delete sibling.noparents;

  deepCopy[currentIdx] = current;
  deepCopy[siblingIdx] = sibling;

  const touched = new Set([currentPid, siblingPid]);

  // I1: each shared parent must list both children.
  [fatherId, motherId].forEach((parentPid) => {
    if (parentPid == null) return;
    const idx = deepCopy.findIndex((p) => p.pid === parentPid);
    if (idx < 0) return;
    const parent = { ...deepCopy[idx] };
    parent.childrenIds = uniq([
      ...(parent.childrenIds || []),
      currentPid,
      siblingPid
    ]);
    deepCopy[idx] = parent;
    touched.add(parentPid);
  });

  // I4: every member sharing >=1 parent with the new sibship is a mutual sibling.
  if (fatherId != null || motherId != null) {
    const sibPids = deepCopy
      .filter(
        (p) =>
          (fatherId != null && p.fatherId === fatherId) ||
          (motherId != null && p.motherId === motherId)
      )
      .map((p) => p.pid);
    sibPids.forEach((pid) => {
      const idx = deepCopy.findIndex((p) => p.pid === pid);
      const member = { ...deepCopy[idx] };
      member.siblingIds = uniq([
        ...(member.siblingIds || []),
        ...sibPids.filter((s) => s !== pid)
      ]);
      deepCopy[idx] = member;
      touched.add(pid);
    });
  }

  return { members: deepCopy, touchedPids: [...touched] };
};

// Remove a partner entry from a spouseRelationships map (object keyed by spouse
// pid). Returns the SAME reference when there is nothing to remove (so callers
// can cheaply detect a no-op), otherwise a new object without that key.
export const omitSpouseRelationship = (spouseRelationships, partnerPid) => {
  if (!spouseRelationships || spouseRelationships[partnerPid] == null) {
    return spouseRelationships;
  }
  const next = { ...spouseRelationships };
  delete next[partnerPid];
  return next;
};

// Scrub every reference to `pid` from a SURVIVING member after `pid` is deleted:
// null the parent back-pointers, drop it from the edge arrays, and drop the
// dangling auxiliary references (twinRelations[].withPid + the spouse-keyed
// spouseRelationships entry) so nothing keeps pointing at the removed member.
// Pure — returns a new member object.
export const stripMemberReferencesToPid = (member, pid) => {
  const next = {
    ...member,
    motherId: member.motherId === pid ? null : member.motherId,
    fatherId: member.fatherId === pid ? null : member.fatherId,
    spouseIds: (member.spouseIds || []).filter((id) => id !== pid),
    childrenIds: (member.childrenIds || []).filter((id) => id !== pid),
    siblingIds: (member.siblingIds || []).filter((id) => id !== pid)
  };
  if (Array.isArray(member.twinRelations)) {
    next.twinRelations = member.twinRelations.filter((r) => r.withPid !== pid);
  }
  const sr = omitSpouseRelationship(member.spouseRelationships, pid);
  if (sr !== member.spouseRelationships) next.spouseRelationships = sr;
  return next;
};

// Read-only integrity analyser (mirror of backend analyzeEdges) — exported for
// tests/dev assertions. Returns the violated edges by category.
export const analyzeMemberEdges = (members) => {
  const list = Array.isArray(members) ? members : [];
  const byPid = new Map();
  list.forEach((m) => {
    if (m && m.pid != null) byPid.set(m.pid, m);
  });

  const desired = new Map(); // parentPid -> Set<childPid> from back-pointers
  list.forEach((m) => {
    if (!m || m.pid == null) return;
    ['fatherId', 'motherId'].forEach((role) => {
      const pp = m[role];
      if (pp == null || !byPid.has(pp)) return;
      if (!desired.has(pp)) desired.set(pp, new Set());
      desired.get(pp).add(m.pid);
    });
  });

  const forwardMissing = [];
  const backMissing = [];
  const garbageChildren = [];
  const spouseAsymmetry = [];

  list.forEach((m) => {
    if (!m || m.pid == null) return;
    const have = new Set((m.childrenIds || []).filter((x) => x != null));
    const want = desired.get(m.pid) || new Set();
    want.forEach((c) => {
      if (!have.has(c)) forwardMissing.push({ parentPid: m.pid, childPid: c });
    });
    have.forEach((c) => {
      if (!byPid.has(c))
        garbageChildren.push({ parentPid: m.pid, childPid: c });
      else if (!want.has(c))
        backMissing.push({ parentPid: m.pid, childPid: c });
    });
    (m.spouseIds || []).forEach((b) => {
      const other = byPid.get(b);
      if (other && !(other.spouseIds || []).includes(m.pid)) {
        spouseAsymmetry.push({ pid: m.pid, spousePid: b });
      }
    });
  });

  return { forwardMissing, backMissing, garbageChildren, spouseAsymmetry };
};
